import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';

const Projects = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('buildings');

  const projectCategories = [
    {
      id: 'buildings',
      label: t('buildingsInfra'),
      projects: [
        {
          title: language === 'en' ? 'BUILDINGS' : 'المباني',
          description: language === 'en' ? 'Comprehensive EPC delivery for high-rise mixed-use facilities and commercial complexes.' : 'تسليم EPC شامل لمرافق متعددة الاستخدامات شاهقة الارتفاع والمجمعات التجارية.',
          image: '/assets/images/buildings-project.jpg'
        },
        {
          title: language === 'en' ? 'VILLAS' : 'الفلل',
          description: language === 'en' ? 'High-end villa construction with premium finishes and landscaping.' : 'بناء فلل راقية مع تشطيبات فاخرة وتنسيق حدائق.',
          image: '/assets/images/villas-project.jpg'
        },
        {
          title: language === 'en' ? 'INFRASTRUCTURE & UTILITIES' : 'البنية التحتية والمرافق',
          description: language === 'en' ? 'Complete infrastructure and distribution systems supporting Saudi Vision 2030.' : 'أنظمة البنية التحتية والتوزيع الكاملة دعماً لرؤية السعودية 2030.',
          image: '/assets/images/infrastructure-project.jpg'
        },
        {
          title: language === 'en' ? 'MEGA-DEVELOPMENTS' : 'التطويرات الضخمة',
          description: language === 'en' ? 'Large-scale integrated city development supporting Kingdom transformation.' : 'تطوير مدينة متكامل واسع النطاق يدعم تحول المملكة.',
          image: '/assets/images/mega-developments-project.jpg'
        },
      ]
    },
    {
      id: 'industrial',
      label: t('industrialEnergy'),
      projects: [
        {
          title: language === 'en' ? 'RENEWABLE ENERGY' : 'الطاقة المتجددة',
          description: language === 'en' ? 'Solar, wind, and renewable energy infrastructure for sustainable power generation.' : 'البنية التحتية للطاقة الشمسية والرياح والطاقة المتجددة لتوليد الطاقة المستدامة.',
          image: '/assets/images/sustainability-division.jpg'
        },
        {
          title: language === 'en' ? 'ENERGY STORAGE' : 'تخزين الطاقة',
          description: language === 'en' ? 'Battery storage systems and energy management solutions for grid stability.' : 'أنظمة تخزين البطاريات وحلول إدارة الطاقة لاستقرار الشبكة.',
          image: '/assets/images/energy-storage-project.jpg'
        },
        {
          title: language === 'en' ? 'ELECTRICAL SUBSTATIONS' : 'المحطات الفرعية الكهربائية',
          description: language === 'en' ? 'Complete design, procurement, and construction of power distribution facilities.' : 'تصميم وشراء وبناء كامل لمرافق توزيع الطاقة.',
          image: '/assets/images/electrical-substations-project.jpg'
        },
        {
          title: language === 'en' ? 'GAS & COGENERATION POWER' : 'الغاز والتوليد المشترك للطاقة',
          description: language === 'en' ? 'Natural gas and combined heat and power (CHP) plant installations.' : 'تركيبات محطات الغاز الطبيعي والحرارة والطاقة المشتركة (CHP).',
          image: '/assets/images/gas-cogeneration-project.jpg'
        },
        {
          title: language === 'en' ? 'DESALINATION & WATER TREATMENT' : 'تحلية ومعالجة المياه',
          description: language === 'en' ? 'Water desalination plants and advanced treatment systems for industrial use.' : 'محطات تحلية المياه وأنظمة المعالجة المتقدمة للاستخدام الصناعي.',
          image: '/assets/images/desalination-water-treatment.jpg'
        },
        {
          title: language === 'en' ? 'WASTE TREATMENT' : 'معالجة النفايات',
          description: language === 'en' ? 'Wastewater treatment facilities and industrial waste management systems.' : 'مرافق معالجة مياه الصرف الصحي وأنظمة إدارة النفايات الصناعية.',
          image: '/assets/images/waste-treatment-project.jpg'
        },
        {
          title: language === 'en' ? 'DISTRICT COOLING' : 'التبريد المركزي',
          description: language === 'en' ? 'Centralized cooling systems for large-scale developments and industrial complexes.' : 'أنظمة التبريد المركزية للتطويرات واسعة النطاق والمجمعات الصناعية.',
          image: '/assets/images/district-cooling-new.jpg'
        },
        {
          title: language === 'en' ? 'OIL & GAS AND PETROCHEMICAL' : 'النفط والغاز والبتروكيماويات',
          description: language === 'en' ? 'Complete EPC services for oil, gas, and petrochemical processing facilities.' : 'خدمات EPC كاملة لمرافق معالجة النفط والغاز والبتروكيماويات.',
          image: '/assets/images/oil-gas-petrochemical.jpg'
        },
      ]
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero */}
      <section className="bg-[#141F48] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('projectsTitle')}</h1>
          <p className="text-xl text-[#D3BC8D] font-medium">{t('projectsSubtitle')}</p>
        </div>
      </section>

      {/* Projects Tabs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-xl mx-auto grid-cols-2 mb-12">
              {projectCategories.map((category) => (
                <TabsTrigger key={category.id} value={category.id} className="text-sm md:text-base">
                  {category.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {projectCategories.map((category) => (
              <TabsContent key={category.id} value={category.id}>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {category.projects.map((project, index) => (
                    <Card key={index} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#141F48] via-[#141F48]/50 to-transparent opacity-80" />
                      </div>
                      <CardContent className="p-6">
                        <h3 className="text-xl font-bold text-[#141F48] mb-3">{project.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{project.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Projects;